class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector("details");
    this.content =
      this.mainDetailsToggle.querySelector("summary").nextElementSibling;

    this.mainDetailsToggle.addEventListener(
      "focusout",
      this.onFocusOut.bind(this),
    );
    this.mainDetailsToggle.addEventListener("toggle", this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute("open")) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute("open");
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
  }
}

customElements.define("details-disclosure", DetailsDisclosure);

/* Een header-submenu KOMT OP met een keyframe-animatie (animateMenuOpen), maar
   ging per direct weg: sluiten is het weghalen van `open`, en de inhoud van een
   <details> is dan op datzelfde moment niet meer gerenderd - geen CSS haalt dat
   terug. Het menu kwam dus zacht op en knipperde eruit.

   De aanpak hieronder is dezelfde als die van Dawn's eigen mobiele drawer
   (`menu-opening` + wachten op transitionend in global.js): `open` blijft staan
   zolang de sluitbeweging duurt, met een klasse erbij die de eindstand zet.

   Waarom een MutationObserver en geen klikhandler: er sluiten VIER dingen dit
   menu - een klik op de summary, `close()` bij focusout, Escape via
   onKeyUpEscape() in global.js, en de header zelf bij verbergen. Ze hebben één
   ding gemeen: `open` verdwijnt. Dat is dus het enige punt waar dit hoeft te
   hangen. De callback loopt als microtask, vóór de browser tekent, dus het
   terugzetten van `open` is onzichtbaar.

   Wat er precies voor nodig is om de beweging op gang te krijgen staat bij
   startClosing() - dat luistert nauwer dan "zet er een klasse op". */
const HEADER_MENU_CLOSING_CLASS = "menu-closing";
/* Alleen een noodrem voor als er geen transitionend komt (een submenu dat nooit
   zichtbaar werd, een browser die de transitie overslaat). Ruim boven de
   --duration-default van 200ms, want dit is niet de timing zelf. */
const HEADER_MENU_CLOSING_FALLBACK_MS = 500;

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector(".header-wrapper");
    /* Ook de submenu's ín het submenu: die zijn gewone <details> zonder eigen
       custom element, dus ze komen alleen langs via deze querySelectorAll. */
    this.querySelectorAll("details").forEach((details) =>
      this.holdCloseAnimation(details),
    );
  }

  holdCloseAnimation(details) {
    const summary = details.querySelector("summary");
    const content = summary && summary.nextElementSibling;
    if (!content) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const state = { closing: false, timer: 0 };
    const watch = { attributes: true, attributeFilter: ["open"] };

    const onTransitionEnd = (event) => {
      if (event.target === content && event.propertyName === "opacity")
        finish();
    };

    const stopClosing = () => {
      state.closing = false;
      clearTimeout(state.timer);
      content.removeEventListener("transitionend", onTransitionEnd);
      details.classList.remove(HEADER_MENU_CLOSING_CLASS);
      content.style.animation = "";
      content.style.transition = "";
      content.style.opacity = "";
      content.style.transform = "";
    };

    /* Waarom deze twee stappen en niet gewoon de klasse erop:
       animateMenuOpen zet met fill-mode forwards opacity op 1, en als die
       animatie in dezelfde stijlberekening verdwijnt als waarin de klasse de
       eindstand zet, ziet de browser geen verandering om overheen te bewegen -
       hij rekent vanaf de waarde ZONDER animatie, en dat is de eindstand zelf.
       Gemeten: het menu sprong dan alsnog in één keer weg.

       Dus eerst de open stand vastzetten in inline stijl (zonder animatie,
       zonder transitie), dan één keer de layout laten uitrekenen, en pas daarna
       die inline waarden weghalen. Nu is er wel twee keer een stijl, met een
       verschil ertussen, en loopt de transitie uit de klasse. */
    const startClosing = () => {
      content.style.animation = "none";
      content.style.transition = "none";
      content.style.opacity = "1";
      content.style.transform = "translateY(0)";
      void content.offsetHeight;

      details.classList.add(HEADER_MENU_CLOSING_CLASS);
      content.style.transition = "";
      content.style.opacity = "";
      content.style.transform = "";
    };

    const finish = () => {
      stopClosing();
      /* Nu echt dicht. De observer moet hier even los: hij ziet ook ONZE
         verwijdering van `open` en zou het menu meteen weer openzetten. Een
         vlag helpt niet - de callback loopt als microtask, dus die is al weer
         uitgezet voordat hij aan de beurt komt. `disconnect()` gooit de
         wachtende meldingen wel weg, dus dit is het enige dat sluit. */
      observer.disconnect();
      details.removeAttribute("open");
      observer.observe(details, watch);
    };

    /* Klik tijdens het weggaan: dat is "toch openhouden", niet "nog een keer
       sluiten". Zonder dit zou de klik `open` weghalen en de beweging opnieuw
       starten. */
    summary.addEventListener("click", (event) => {
      if (!state.closing) return;
      event.preventDefault();
      stopClosing();
    });

    const observer = new MutationObserver(() => {
      if (
        state.closing ||
        details.hasAttribute("open") ||
        reducedMotion.matches
      ) {
        return;
      }
      state.closing = true;
      details.setAttribute("open", "");
      startClosing();
      content.addEventListener("transitionend", onTransitionEnd);
      state.timer = setTimeout(finish, HEADER_MENU_CLOSING_FALLBACK_MS);
    });
    observer.observe(details, watch);
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (
      document.documentElement.style.getPropertyValue(
        "--header-bottom-position-desktop",
      ) !== ""
    )
      return;
    document.documentElement.style.setProperty(
      "--header-bottom-position-desktop",
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`,
    );
  }
}

customElements.define("header-menu", HeaderMenu);
